import type { Board, CastlingRights, Color, GameState, Piece, PieceType, Square } from "./types";

export function fileOf(square: Square): number {
  return square & 7;
}

export function rankOf(square: Square): number {
  return square >> 3;
}

export function squareAt(file: number, rank: number): Square {
  return rank * 8 + file;
}

export function isOnBoard(file: number, rank: number): boolean {
  return file >= 0 && file < 8 && rank >= 0 && rank < 8;
}

const FILES = "abcdefgh";

export function squareToAlgebraic(square: Square): string {
  return `${FILES[fileOf(square)]}${rankOf(square) + 1}`;
}

export function algebraicToSquare(algebraic: string): Square {
  const file = FILES.indexOf(algebraic[0] ?? "");
  const rank = Number(algebraic[1]) - 1;
  return squareAt(file, rank);
}

const BACK_RANK: readonly PieceType[] = ["r", "n", "b", "q", "k", "b", "n", "r"];

export function createInitialBoard(): Board {
  const board: (Piece | null)[] = new Array(64).fill(null);
  for (let file = 0; file < 8; file++) {
    const backPiece = BACK_RANK[file];
    if (!backPiece) continue;
    board[squareAt(file, 0)] = { type: backPiece, color: "w" };
    board[squareAt(file, 1)] = { type: "p", color: "w" };
    board[squareAt(file, 6)] = { type: "p", color: "b" };
    board[squareAt(file, 7)] = { type: backPiece, color: "b" };
  }
  return board;
}

export const INITIAL_CASTLING: CastlingRights = { wk: true, wq: true, bk: true, bq: true };

export function createInitialState(): GameState {
  return {
    board: createInitialBoard(),
    turn: "w",
    castling: INITIAL_CASTLING,
    enPassantSquare: null,
    halfmoveClock: 0,
    fullmoveNumber: 1,
  };
}

export function cloneBoard(board: Board): (Piece | null)[] {
  return board.slice();
}

export function opponentOf(color: Color): Color {
  return color === "w" ? "b" : "w";
}

export function findKing(board: Board, color: Color): Square {
  const square = board.findIndex((piece) => piece?.type === "k" && piece.color === color);
  if (square < 0) throw new Error(`Roi ${color} introuvable sur le plateau`);
  return square;
}

/** Construit un GameState a partir d'une chaine FEN (utilise surtout par les tests, pour ecrire des positions lisibles). */
export function parseFen(fen: string): GameState {
  const [boardPart, turnPart, castlingPart, epPart, halfmovePart, fullmovePart] = fen.trim().split(/\s+/);
  const board: (Piece | null)[] = new Array(64).fill(null);
  const ranks = (boardPart ?? "").split("/");
  for (let i = 0; i < 8; i++) {
    const rank = 7 - i;
    let file = 0;
    for (const ch of ranks[i] ?? "") {
      if (/[1-8]/.test(ch)) {
        file += Number(ch);
        continue;
      }
      const color: Color = ch === ch.toUpperCase() ? "w" : "b";
      board[squareAt(file, rank)] = { type: ch.toLowerCase() as PieceType, color };
      file++;
    }
  }
  const castling: CastlingRights = {
    wk: (castlingPart ?? "").includes("K"),
    wq: (castlingPart ?? "").includes("Q"),
    bk: (castlingPart ?? "").includes("k"),
    bq: (castlingPart ?? "").includes("q"),
  };
  return {
    board,
    turn: turnPart === "b" ? "b" : "w",
    castling,
    enPassantSquare: epPart && epPart !== "-" ? algebraicToSquare(epPart) : null,
    halfmoveClock: halfmovePart ? Number(halfmovePart) : 0,
    fullmoveNumber: fullmovePart ? Number(fullmovePart) : 1,
  };
}

/** Cle de position pour la detection de repetition (plateau + trait + roques + prise en passant possible). */
export function positionKey(state: GameState): string {
  const boardKey = state.board.map((p) => (p ? `${p.color}${p.type}` : "-")).join("");
  const castlingKey = `${state.castling.wk ? "K" : ""}${state.castling.wq ? "Q" : ""}${state.castling.bk ? "k" : ""}${state.castling.bq ? "q" : ""}`;
  const epKey = state.enPassantSquare ?? "-";
  return `${boardKey}|${state.turn}|${castlingKey}|${epKey}`;
}
